import React, { useState } from "react";
import { Link } from "react-router-dom";
import Button from "@mui/material/Button";
import Typography from "@mui/material/Typography";
import Box from "@mui/material/Box";
import Form from "react-bootstrap/Form";
import OtpInput from "react-otp-input";
import { toast } from "react-toastify";

import colorSchemes from "../../../constants/colorSchemes";
import useResendCountdown, {
  RESEND_WAIT_SECONDS,
  formatCountdown,
} from "../../../common/useResendCountdown";

export const errorText = (error, fallback = "Something went wrong!") =>
  error?.response?.data?.error || fallback;

// The 6-digit code step used at sign-up and when confirming, changing or
// adding an email: the code boxes, [Confirm] and "Send a new code" with the
// 120-second wait. onConfirm(code) and onResend() return the API promise.
export default function EmailCodeBox({
  email,
  onConfirm,
  onResend,
  waitSeconds = RESEND_WAIT_SECONDS,
  confirmLabel = "Confirm",
  children,
}) {
  const [code, setCode] = useState("");
  const [isBusy, setIsBusy] = useState(false);
  const [resendSecondsLeft, setResendSecondsLeft] = useResendCountdown(waitSeconds);

  const confirm = async (event) => {
    event.preventDefault();
    if (isBusy) return;
    if (code.length !== 6) {
      toast("The code is the 6 digits from the email.");
      return;
    }
    setIsBusy(true);
    try {
      await onConfirm(code);
    } catch (error) {
      toast(errorText(error));
      setCode("");
    }
    setIsBusy(false);
  };

  const resend = async (event) => {
    event.preventDefault();
    if (isBusy) return;
    setIsBusy(true);
    try {
      await onResend();
      toast("A new code has been sent. The old code no longer works.");
      setCode("");
      setResendSecondsLeft(RESEND_WAIT_SECONDS);
    } catch (error) {
      toast(errorText(error));
      if (error?.response?.data?.waitSeconds) {
        setResendSecondsLeft(error.response.data.waitSeconds);
      }
    }
    setIsBusy(false);
  };

  return (
    <form onSubmit={confirm}>
      <Typography sx={{ color: "#7E8184", fontSize: "14px", margin: "1em 0" }}>
        We have sent a 6-digit code to <strong>{email}</strong>. It works for
        10 minutes.
      </Typography>
      <Form.Group className="mb-3 otp" controlId="formEmailCode">
        <Form.Label style={{ color: "#7E8184" }}>Code</Form.Label>
        <OtpInput
          value={code}
          onChange={setCode}
          numInputs={6}
          inputType="tel"
          shouldAutoFocus
          renderSeparator={<span>-</span>}
          renderInput={(props) => <input {...props} />}
        />
      </Form.Group>
      <Typography sx={{ fontSize: "14px", margin: "1em 0" }}>
        {resendSecondsLeft > 0 ? (
          <span style={{ color: "#9A9DA0" }}>
            Send a new code in {formatCountdown(resendSecondsLeft)}
          </span>
        ) : (
          <Link to="#" onClick={resend}>
            Send a new code
          </Link>
        )}
      </Typography>
      <Box>
        <Button
          sx={{
            backgroundColor: colorSchemes.primaryColor,
            color: "#fff",
            "&.MuiButtonBase-root:hover": { bgcolor: "#2D55EB !important" },
          }}
          fullWidth
          type="submit"
          disabled={isBusy}
        >
          {confirmLabel}
        </Button>
      </Box>
      {children}
    </form>
  );
}
